import * as React from 'react';
import { SyntheticEvent } from 'react';
import { Groups } from '../../Enum/Groups';
import { FilterInterface } from '../../Filter/FilterInterface';
import { Attribute } from '../../Model/Attribute';
import { ProductInterface } from '../../Model/Product';
import { ObjectUtility } from '../../Service/ObjectUtility';
import { FilterDisableEvent } from '../Filter/FilterDisableEvent';
import { FilterEvent } from '../Filter/FilterEvent';
import { OptionsCollection } from '../Filter/OptionsCollection';

export interface FilterSectionViewProps {
    rootProducts: ProductInterface[],
    allProducts: ProductInterface[],
    filteredProducts: ProductInterface[],
    group: Groups,
    filters: Map<string, FilterInterface<any>>,
    onFilterChange: <F>(attributeKey: string, filter: FilterInterface<F>) => void,
    onIgnoreFilterChange: (event: SyntheticEvent) => void
}

interface FilterSectionViewInterface {
    /**
     * Collect all Attributes for `key` of the props `allProducts` and create a OptionsCollection from them
     *
     * @param key
     */
    collect<OPTION_TYPE>(key: string): OptionsCollection<OPTION_TYPE>;

    /**
     * Collect all Attributes for `key` of the given products and create a OptionsCollection from them
     *
     * @param products
     * @param key
     */
    collect<OPTION_TYPE>(products: ProductInterface[], key: string): OptionsCollection<OPTION_TYPE>;
}

export abstract class AbstractFilterSectionView<T extends FilterSectionViewProps>
    extends React.Component<T>
    implements FilterSectionViewInterface {
    constructor(props: Readonly<T>) {
        super(props);
        this.handleChange = this.handleChange.bind(this);
    }

    public handleChange<F>(event: FilterEvent<F, any>, id: string, filter: FilterInterface<F>) {
        console.log(`[AbstractFilterSectionView] Filter "${id}" changed`);
        this.props.onFilterChange(
            id,
            this.updateFilters(event, id, filter)
        );
    }

    public collect<OPTION_TYPE>(
        productsOrKey: ProductInterface[] | string,
        key?: string
    ): OptionsCollection<OPTION_TYPE> {
        if (typeof productsOrKey === 'string') {
            return this.collect(this.props.allProducts, productsOrKey);
        } else if (key) {
            return OptionsCollection.from(
                productsOrKey.flatMap(p => ObjectUtility.valueForKeyPath(p, key) || []) as Attribute[] | string[]
            );
        } else {
            throw new TypeError(
                'Invalid method call. Use `collect(key: string)` or `collect(products: ProductInterface[], key: string)`'
            );
        }
    }

    public collectAvailable<OPTION_TYPE>(key: string): OptionsCollection<OPTION_TYPE> {
        const all = this.collect<OPTION_TYPE>(this.props.allProducts, key);
        const filtered = this.collect<OPTION_TYPE>(this.props.filteredProducts, key);

        const optionsCollection = new OptionsCollection(all.map(value => {
            const isAvailable = filtered.contains(value.name);

            return value.withIsAvailable(isAvailable);
        }));

        return optionsCollection.localeSort();
    }

    protected getFilter<VALUE_TYPE = any,
        FILTER_TYPE extends FilterInterface<VALUE_TYPE> = FilterInterface<VALUE_TYPE>>
    (key: string): FILTER_TYPE {
        const filters = this.props.filters;

        if (!filters.has(key)) {
            throw new ReferenceError(`No filter with ID ${key} found`);
        }

        return filters.get(key) as FILTER_TYPE;
    }

    protected updateFilters<F>(
        event: FilterEvent<F, any>,
        id: string,
        filter: FilterInterface<F>
    ): FilterInterface<any> {
        if (!filter) {
            throw new ReferenceError('No Filter given');
        }

        if (event instanceof FilterDisableEvent) {
            return filter.cleared();
        }

        const eventValue = event.value;

        return filter
            .withValue(eventValue as F)
            .withActive(!!eventValue);
    }
}
